/**
 * Case Selector Component
 * Smart autocomplete component for selecting cases
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  Autocomplete,
  TextField,
  Box,
  Typography,
  Chip,
  Avatar,
  Paper,
  ListItem,
  ListItemAvatar,
  ListItemText,
  CircularProgress,
  InputAdornment,
  Alert
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Search as SearchIcon,
  Assignment as CaseIcon,
  CalendarToday as CalendarIcon,
  Person as PersonIcon
} from '@mui/icons-material';

import caseService, { CaseData } from '../../services/caseManagementService';

// Styled components
const CaseOptionContainer = styled(ListItem)(({ theme }) => ({
  padding: theme.spacing(1),
  borderRadius: theme.shape.borderRadius,
  '&:hover': {
    backgroundColor: theme.palette.action.hover
  }
}));

const CaseInfo = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5)
}));

const CaseMetadata = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  marginTop: theme.spacing(0.5)
}));

interface CaseSelectorProps {
  value?: CaseData | null;
  onChange: (case_: CaseData | null) => void;
  placeholder?: string;
  disabled?: boolean;
  fullWidth?: boolean;
  error?: boolean;
  helperText?: string;
  size?: 'small' | 'medium';
}

const CaseSelector: React.FC<CaseSelectorProps> = ({
  value,
  onChange,
  placeholder = "Search for cases...",
  disabled = false,
  fullWidth = true,
  error = false,
  helperText,
  size = 'medium'
}) => {
  const [cases, setCases] = useState<CaseData[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [hasSearched, setHasSearched] = useState(false);

  // Load cases when component mounts or search term changes
  useEffect(() => {
    const loadCases = async () => {
      if (searchTerm.length < 2 && !hasSearched) {
        return;
      }

      setLoading(true);
      try {
        let result;
        if (searchTerm.length >= 2) {
          // Search with term
          result = await caseService.getCases({ 
            search: searchTerm,
            limit: 20
          });
          setHasSearched(true);
        } else {
          // Load recent cases
          result = await caseService.getCases({ 
            limit: 50 
          });
          setHasSearched(true);
        }
        
        if (result.success && result.data) {
          setCases(result.data);
        } else {
          setCases([]);
        }
      } catch (error) {
        console.error('Failed to load cases:', error);
        setCases([]);
      } finally {
        setLoading(false);
      }
    };

    const timeoutId = setTimeout(loadCases, 300); // Debounce search
    return () => clearTimeout(timeoutId);
  }, [searchTerm, hasSearched]);

  // Filter and sort cases based on search term
  const filteredCases = useMemo(() => {
    if (!searchTerm || searchTerm.length < 2) {
      return cases.slice(0, 20); // Show recent cases
    }

    const term = searchTerm.toLowerCase();
    return cases
      .filter(case_ => 
        (case_.case_number && case_.case_number.toLowerCase().includes(term)) ||
        (case_.title && case_.title.toLowerCase().includes(term)) ||
        (case_.customer_name && case_.customer_name.toLowerCase().includes(term))
      )
      .sort((a, b) => {
        // Prioritize exact case number matches
        const aNumberMatch = a.case_number && a.case_number.toLowerCase().includes(term);
        const bNumberMatch = b.case_number && b.case_number.toLowerCase().includes(term);
        
        if (aNumberMatch && !bNumberMatch) return -1;
        if (!aNumberMatch && bNumberMatch) return 1;
        
        // Then by creation date (newest first)
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      })
      .slice(0, 20);
  }, [cases, searchTerm]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit'
    });
  };

  const getCaseStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'open': return 'primary';
      case 'in_progress': return 'warning';
      case 'waiting': return 'info';
      case 'closed': return 'success';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getCaseAvatar = (case_: CaseData) => {
    const caseType = case_.case_type || 'general';
    const letter = caseType.charAt(0).toUpperCase();
    
    return (
      <Avatar sx={{ width: 32, height: 32, fontSize: '0.875rem' }}>
        {letter}
      </Avatar>
    );
  };

  return (
    <Box>
      <Autocomplete
        options={filteredCases}
        value={value}
        onChange={(_, newValue) => onChange(newValue)}
        onInputChange={(_, newInputValue) => setSearchTerm(newInputValue)}
        getOptionLabel={(option) => `${option.case_number || option.id} - ${option.title || 'Untitled'}`}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        loading={loading}
        disabled={disabled}
        fullWidth={fullWidth}
        size={size}
        clearOnBlur={false}
        clearOnEscape
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={placeholder}
            error={error}
            helperText={helperText}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(props, option) => (
          <CaseOptionContainer {...props} key={option.id}>
            <ListItemAvatar>
              {getCaseAvatar(option)}
            </ListItemAvatar>
            <ListItemText
              primary={
                <Box>
                  <Typography variant="subtitle2" component="span" fontWeight={600}>
                    {option.case_number || option.id}
                  </Typography>
                  <Typography variant="body2" component="span" sx={{ ml: 1 }}>
                    {option.title || 'Untitled'}
                  </Typography>
                </Box>
              }
              secondary={
                <CaseMetadata>
                  <Chip 
                    label={option.status} 
                    color={getCaseStatusColor(option.status) as any}
                    size="small"
                    sx={{ fontSize: '0.75rem', height: 20 }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <CalendarIcon sx={{ fontSize: 12 }} />
                    {formatDate(option.created_at)}
                  </Typography>
                  {option.customer_name && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <PersonIcon sx={{ fontSize: 12 }} />
                      {option.customer_name}
                    </Typography>
                  )}
                </CaseMetadata>
              }
            />
          </CaseOptionContainer>
        )}
        PaperComponent={({ children, ...other }) => (
          <Paper {...other} sx={{ mt: 1 }}>
            {!hasSearched && filteredCases.length === 0 && !loading && (
              <Alert severity="info" sx={{ m: 1 }}>
                Start typing to search for cases...
              </Alert>
            )}
            {hasSearched && filteredCases.length === 0 && !loading && searchTerm.length >= 2 && (
              <Alert severity="warning" sx={{ m: 1 }}>
                No cases found matching "{searchTerm}"
              </Alert>
            )}
            {children}
          </Paper>
        )}
        noOptionsText={
          searchTerm.length < 2 
            ? "Type at least 2 characters to search" 
            : `No cases found for "${searchTerm}"`
        }
      />
      
      {value && (
        <Box sx={{ mt: 1, p: 1, backgroundColor: 'action.hover', borderRadius: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Selected: <strong>{value.case_number || value.id}</strong> - {value.title || 'Untitled'}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default CaseSelector;
